"use client";

import { format } from "date-fns";
import { useTranslations } from "next-intl";
import React from "react";

import AccountTypeIcon from "@/components/account-type-icon";
import BeneficiaryBankDetails from "@/components/beneficiary-bank-details";
import CollapsibleDetailSection from "@/components/collapsible-detail-section";
import DetailRow from "@/components/detail-row";
import FormattedCurrencyAmount from "@/components/formatted-currency-amount";
import { LEDGER_ENTRY_DATE_TIME_LONG_FORMAT } from "@/constants";
import {
  DEBIT_REASON,
  PAYMENT_CANCELLATION_REASON,
  PENDING_PAYMENT_REASON,
  WITHDRAWAL_REASON_PAYMENT_REVERSAL,
} from "@/constants/ledgerReasons";
import { usePurposeCodeLabel } from "@/utils/formatPurposeCode";
import { getLegalEntityName } from "@/utils/getLegalEntityName";
import { FilteredBeneficiaryDetail, InstructionState } from "@generated/types";

import InstructionStatusBadge from "../instruction-status-badge";
import TransactionStatusBadge, {
  TransactionStatusBadgeType,
} from "../transaction-status-badge";

import LedgerEntryBaseFields from "./ledger-entry-base-fields";
import { MissingResolutionAlert } from "./missing-resolution-alert";

const PROCESSING_STATES = [
  "ACCEPTED",
  "WAITING",
  "READY",
  "PROCESSING",
  "PENDING",
  "UNRECONCILED",
  "SCHEDULED",
  "FUNDS_CONFIRMED",
] as const;

interface PaymentDetailsProps {
  entry: LedgerEntryResolvedPayments;
  showInstructionStatus?: boolean;
  amountLabel?: string;
  statusOverride?: TransactionStatusBadgeType;
}

export default function PaymentDetails({
  entry,
  showInstructionStatus = false,
  amountLabel,
  statusOverride,
}: PaymentDetailsProps) {
  const tCommon = useTranslations("common");
  const tLedgerFields = useTranslations("ledger.fields");
  const tEnums = useTranslations("enums");
  const tBankAccount = useTranslations("bankAccount");
  const tPay = useTranslations("pay");
  const purposeCodeLabel = usePurposeCodeLabel();

  // BFF resolution failed (graceful degradation). Render whatever the base
  // ledger entry already carries plus a support recovery prompt.
  if (!entry.resolvedData) {
    return (
      <div className="space-y-0">
        <LedgerEntryBaseFields entry={entry} />
        <MissingResolutionAlert />
      </div>
    );
  }

  // Narrow beneficiary: string means UUID fallback (resolution failed)
  const beneficiary =
    typeof entry.resolvedData.beneficiary === "string"
      ? undefined
      : entry.resolvedData.beneficiary;

  const isFee =
    entry.reason === "fee" &&
    entry.resolvedData.feeAmount &&
    entry.resolvedData.feeAmount > 0;

  const isPaymentReversal =
    entry.reason === WITHDRAWAL_REASON_PAYMENT_REVERSAL ||
    (entry.balance &&
      entry.balance > 0 &&
      entry.reason === PAYMENT_CANCELLATION_REASON);

  const isDebit =
    entry.reason === DEBIT_REASON ||
    entry.reason === PENDING_PAYMENT_REASON ||
    entry.debit > 0;

  const purposeCodeValue =
    "paymentReason" in entry.resolvedData
      ? entry.resolvedData.paymentReason
      : undefined;

  const displayedStatus = statusOverride
    ? statusOverride
    : showInstructionStatus &&
        "instructionState" in entry.resolvedData &&
        entry.resolvedData.instructionState
      ? entry.resolvedData.instructionState === InstructionState.Unconfirmed
        ? "PROCESSING"
        : null
      : isPaymentReversal
        ? "RETURNED"
        : entry.resolvedData.paymentState;

  const isProcessing =
    !!displayedStatus &&
    PROCESSING_STATES.includes(
      displayedStatus as (typeof PROCESSING_STATES)[number]
    );

  return (
    <div className="space-y-0">
      <DetailRow
        label={tCommon("to")}
        value={getLegalEntityName(
          entry.resolvedData.beneficiary as FilteredBeneficiaryDetail
        )}
        translateValue={false}
      />
      {isPaymentReversal && (
        <DetailRow
          label={tLedgerFields("amountReturned")}
          value={
            <FormattedCurrencyAmount
              balance={entry.balance ?? 0}
              product={entry.product}
            />
          }
          translateValue={false}
        />
      )}

      {isDebit && !isFee && (
        <DetailRow
          label={amountLabel || tLedgerFields("amountSent")}
          value={
            <FormattedCurrencyAmount
              balance={entry.balance ?? 0}
              product={entry.product}
            />
          }
          translateValue={false}
        />
      )}

      {isFee && (
        <DetailRow
          label={tLedgerFields("transactionFee")}
          value={
            <FormattedCurrencyAmount
              balance={entry.resolvedData.feeAmount ?? 0}
              product={entry.resolvedData.feeCurrency ?? ""}
              forceNegative
            />
          }
          translateValue={false}
        />
      )}

      {showInstructionStatus &&
        isDebit &&
        !isFee &&
        entry.resolvedData.feeCurrency && (
          <DetailRow
            label={tLedgerFields("transactionFee")}
            value={
              <FormattedCurrencyAmount
                balance={entry.resolvedData.feeAmount ?? 0}
                product={entry.resolvedData.feeCurrency}
                forceNegative
              />
            }
            translateValue={false}
          />
        )}

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("sentOn")}
        value={format(
          new Date(entry.resolvedData.meta?.createTime || ""),
          LEDGER_ENTRY_DATE_TIME_LONG_FORMAT
        )}
      />

      <DetailRow
        label={tLedgerFields("status")}
        value={
          statusOverride ? (
            <TransactionStatusBadge size="xs" status={statusOverride} />
          ) : showInstructionStatus &&
            "instructionState" in entry.resolvedData &&
            entry.resolvedData.instructionState ? (
            <InstructionStatusBadge
              size="xs"
              status={entry.resolvedData.instructionState}
            />
          ) : (
            <TransactionStatusBadge
              showReconciled
              size="xs"
              status={
                isPaymentReversal
                  ? ("RETURNED" as TransactionStatusBadgeType)
                  : (entry.resolvedData
                      .paymentState as TransactionStatusBadgeType)
              }
            />
          )
        }
      />

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("purposeCode")}
        value={purposeCodeLabel(purposeCodeValue) ?? "-"}
      />

      <DetailRow
        label={tLedgerFields("senderReference")}
        value={entry?.resolvedData?.remittanceInformation ?? "-"}
        translateValue={false}
      />

      <hr className="my-2 opacity-65" />

      <CollapsibleDetailSection
        title={tBankAccount("header")}
        showLabel={tBankAccount("viewDetails")}
        hideLabel={tBankAccount("hideDetails")}
      >
        <div className="space-y-0">
          <DetailRow
            label={tLedgerFields("payeeType")}
            value={
              <AccountTypeIcon
                type={beneficiary?.beneficiaryType || ""}
                label={tEnums(
                  `BeneficiaryType.${beneficiary?.beneficiaryType}`
                )}
                showLabel={true}
                size={14}
              />
            }
          />

          <BeneficiaryBankDetails
            beneficiary={beneficiary as FilteredBeneficiaryDetail}
            singleColumn
          />
        </div>
      </CollapsibleDetailSection>

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("referenceId")}
        value={entry.resolvedData.reference}
        translateValue={false}
      />

      {isProcessing && (
        <div className="w-full mt-4 rounded-lg border border-brand-primary/30 bg-brand-primary/5 px-4 py-3 text-center text-xs font-medium text-gray-900">
          {tPay("paymentTimings")}
        </div>
      )}
    </div>
  );
}
